import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';

type Props = {
  onBase64Change?: (base64: string) => void;
};

export type ImageUploaderHandle = {
  openFileDialog: () => void;
  clearImage: () => void;
};

const ImageUploader = forwardRef<ImageUploaderHandle, Props>(({ onBase64Change }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setPreviewUrl(reader.result);
        if (onBase64Change) {
          onBase64Change(reader.result);
        }
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, []);

  const onClick = (isClear: boolean = false) => {
    if (isClear) {
      setPreviewUrl(null);
      if (onBase64Change) {
        onBase64Change('');
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    } else {
      inputRef.current?.click();
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    else if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  useImperativeHandle(ref, () => ({
    openFileDialog: () => onClick(false),
    clearImage: () => onClick(true)
  }));

  return (
    <div
      onClick={() => onClick(false)}
      onDrop={onDrop} 
      onDragOver={(e) => e.preventDefault()}
      className={`relative w-full sm:w-[500px] border-[1px] border-dashed border-gray-400 rounded-lg flex items-center justify-center cursor-pointer ${previewUrl ? 'h-[200px]' : 'h-10'}`}
    >
      {previewUrl ? (
        <img src={previewUrl} alt="preview" className="max-h-full max-w-full object-contain my-2" />
      ) : (
        <p className="text-gray-500">画像をアップロード</p>
      )}
      {previewUrl && (
        <div
          id="clear-image"
          style={{ position: 'absolute', top: 4, right: 4 }}
          onClick={(e) => {
            e.stopPropagation();
            onClick(true);
          }}
          className="text-gray-500"
        >
          <img src="/images/icon-close.svg" alt="clear" className="w-6 h-6" />
        </div>
      )}
      <input
        
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileChange}
      />
    </div>
  );
});

export default ImageUploader;
